import { Features } from '@/models/bootstrap/plan';
import { Permissions } from '@/models/user';
import { ChangePassword } from '@/packages/change-password';
import { CorporateUniversityRoutes } from '@/packages/corporate-university/routes';
import { CoursesRoutes } from '@/packages/courses/routes';
import { Dashboard } from '@/packages/dashboard';
import { Dependents } from '@/packages/dependents';
import { Dev } from '@/packages/dev';
import { EduplayRoutes } from '@/packages/eduplay/routes';
import { EmployerRoutes } from '@/packages/employer/routes';
import { Home } from '@/packages/home';
import { AcademicHubRoutes } from '@/packages/academic-hub/routes';
import { IdpRoutes } from '@/packages/idp/routes';
import {
  LearningPathManagementRoutes,
  LearningPathRoutes,
} from '@/packages/learning-path/routes';
import { LMSReport } from '@/packages/lms-report';
import { MyProfile } from '@/packages/my-profile';
import { SkillManagement } from '@/packages/skill-management';
import { SybsidyRoutes } from '@/packages/subsidy/routes';
import { SubsidyManagementRoutes } from '@/packages/subsidy-management/routes';
import { Terms } from '@/packages/terms';
import { VideoLibrary } from '@/packages/video-library';
import type { AppRoute } from '@/routes/interfaces';

enum PrivateRouteItem {
  Home = 'Home',
  Dev = 'Dev',
  Idp = 'Idp',
  LearningPaths = 'LearningPaths',
  Subsidy = 'Subsidy',
  Eduplay = 'Eduplay',
  MyProfile = 'MyProfile',
  ChangePassword = 'ChangePassword',
  Dashboard = 'Dashboard',
  Terms = 'Terms',
  VideoLibrary = 'VideoLibrary',
  Dependents = 'Dependents',
  LmsReport = 'LmsReport',
  SkillManagement = 'SkillManagement',
  EmployerManagement = 'EmployerManagement',
  SubsidyManagement = 'SubsidyManagement',
  LearningPathManagement = 'LearningPathManagement',
  CorporateUniversity = 'CorporateUniversity',
  Courses = 'Courses',
  AcademicHub = 'AcademicHub',
}

export const privateRoutes: Record<PrivateRouteItem, AppRoute> = {
  [PrivateRouteItem.Home]: {
    label: 'Home',
    element: Home,
    home: true,
    path: '/',
  },
  [PrivateRouteItem.Dev]: {
    label: 'DEV',
    element: Dev,
    path: '/dev',
    skipTermsCheck: true,
  },
  [PrivateRouteItem.Courses]: {
    label: 'Cursos',
    element: CoursesRoutes,
    path: '/cursos',
  },
  [PrivateRouteItem.Idp]: {
    label: 'Plano personalizado',
    element: IdpRoutes,
    path: '/pdi/planos',
    features: [Features.Idp],
  },
  [PrivateRouteItem.LearningPaths]: {
    label: 'Trilhas',
    element: LearningPathRoutes,
    path: '/trilhas',
    features: [Features.LearningPaths],
  },
  [PrivateRouteItem.LearningPathManagement]: {
    label: 'Gestão de trilhas',
    element: LearningPathManagementRoutes,
    path: '/empresa/gestao-de-trilhas',
    permissions: [Permissions.HR],
    features: [Features.LearningPaths],
  },
  [PrivateRouteItem.Subsidy]: {
    label: 'Reembolso',
    element: SybsidyRoutes,
    path: '/meus-reembolsos',
    features: [Features.Subsidies],
    check: ({ bootstrap }) =>
      !!bootstrap?.plan?.subsidies_eligibility?.eligible_for_subsidies,
  },
  [PrivateRouteItem.SubsidyManagement]: {
    label: 'Gestão de reembolsos',
    path: '/empresa/gestao-de-reembolsos',
    permissions: [Permissions.HR],
    features: [Features.Subsidies],
    check: ({ bootstrap }) =>
      !!bootstrap?.plan?.subsidies_eligibility?.eligible_assessor,
    element: SubsidyManagementRoutes,
  },
  [PrivateRouteItem.Eduplay]: {
    label: 'Meu aprendizado',
    element: EduplayRoutes,
    path: '/meu-aprendizado',
  },
  [PrivateRouteItem.SkillManagement]: {
    label: 'Gerenciar habilidades',
    element: SkillManagement,
    path: '/gestao-de-habilidades',
  },
  [PrivateRouteItem.MyProfile]: {
    label: 'Meu perfil',
    element: MyProfile,
    path: '/meu-perfil',
  },
  [PrivateRouteItem.ChangePassword]: {
    label: 'Alterar senha',
    element: ChangePassword,
    path: '/alterar-senha',
  },
  [PrivateRouteItem.Dashboard]: {
    label: 'Relatórios',
    element: Dashboard,
    permissions: [Permissions.HR],
    path: '/empresa/dashboard',
  },
  [PrivateRouteItem.Terms]: {
    label: 'Termos de uso',
    element: Terms,
    path: '/termos',
    skipTermsCheck: true,
  },
  [PrivateRouteItem.VideoLibrary]: {
    label: 'Biblioteca de vídeos',
    element: VideoLibrary,
    path: '/videos/:videoId',
  },
  [PrivateRouteItem.LmsReport]: {
    label: 'Relatórios LMS',
    path: '/empresa/relatorios-lms',
    element: LMSReport,
    permissions: [Permissions.HR],
    check: ({ user }) =>
      !!(
        user?.colaborador?.empresa?.logo &&
        user?.colaborador?.empresa?.banner_background &&
        user?.colaborador?.empresa?.feature_lms &&
        user?.colaborador?.empresa?.corporate_university_name &&
        user?.colaborador?.empresa?.primary_color
      ), // hasLMS()
  },
  [PrivateRouteItem.Dependents]: {
    label: 'Meus dependentes',
    path: '/empresa/dependentes',
    element: Dependents,
    check: ({ user }) => !user?.colaborador?.employee_id,
    features: [Features.DependentsIncluded],
  },
  [PrivateRouteItem.EmployerManagement]: {
    label: 'Gestão de colaboradores',
    path: '/empresa/gestao-de-colaboradores',
    element: EmployerRoutes,
    permissions: [Permissions.HR],
    check: ({ user }) => !user?.colaborador?.employee_id,
  },
  [PrivateRouteItem.CorporateUniversity]: {
    label: 'Universidade Corporativa',
    path: '/corporate-university',
    element: CorporateUniversityRoutes,
  },
  [PrivateRouteItem.AcademicHub]: {
    label: 'Hub Acadêmico',
    element: AcademicHubRoutes,
    path: '/hub-academico',
    features: [Features.AcademicSituation],
  },
};
